import { ref, unref } from 'vue';

import type { Ref } from 'vue';

export type TFocusLocation = 'next' | 'previous' | 'first' | 'last';
export interface IUseFocus {
  contentRef: Ref<HTMLElement | null>;
  isFocused: Ref<boolean>;
  onFocusin: (event: FocusEvent) => void;
  onFocusout: (event: FocusEvent) => void;
  onFocus: (event: FocusEvent) => void;
  onKeydown: (event: KeyboardEvent) => void;
  focus: (location?: TFocusLocation) => void;
}

export function useFocus(): IUseFocus {
  const contentRef = ref<HTMLElement | null>(null);
  const isFocused = ref(false);

  function onFocusin(): void {
    isFocused.value = true;
  }

  function onFocusout(): void {
    isFocused.value = false;
  }

  function onFocus(event: FocusEvent): void {
    if (
      !unref(isFocused) &&
      !event.relatedTarget &&
      unref(contentRef)?.contains(event.relatedTarget as unknown as Node)
    ) {
      focus();
    }
  }

  function onKeydown(event: KeyboardEvent): void {
    if (!unref(contentRef)) return;

    switch (event.key) {
      case 'ArrowDown':
        focus('next');
        break;
      case 'ArrowUp':
        focus('previous');
        break;
      case 'Home':
        focus('first');
        break;
      case 'End':
        focus('last');
        break;
      default:
        return;
    }

    event.preventDefault();
  }

  function focus(location?: TFocusLocation): void {
    if (!unref(contentRef)) return;

    const focusable = [
      ...unref(contentRef)!.querySelectorAll(
        'button, input:not([tabindex="-1"]), select, textarea, [href], [tabindex]:not([tabindex="-1"])'
      )
    ].filter((el) => !el.hasAttribute('disabled')) as HTMLElement[];
    const idx = focusable.indexOf(document.activeElement as HTMLElement);

    if (!location) {
      if (!unref(contentRef)!.contains(document.activeElement)) {
        focusable[0]?.focus();
      }
    } else if (location === 'first') {
      focusable[0]?.focus();
    } else if (location === 'last') {
      focusable.at(-1)?.focus();
    } else {
      let el,
        idxx = idx;
      const inc = location === 'next' ? 1 : -1;

      do {
        idxx += inc;
        el = focusable[idxx];
      } while ((!el || el.offsetParent == null) && idxx < focusable.length && idxx >= 0);

      if (el) {
        el.focus();
      } else {
        focus(location === 'next' ? 'first' : 'last');
      }
    }
  }

  return { contentRef, isFocused, onFocus, onFocusin, onFocusout, onKeydown, focus };
}
